import { Component, ViewEncapsulation, OnInit, OnDestroy, ViewChild, HostListener } from '@angular/core';
import { scrollContentWidth } from 'src/shared/components/scroll-wrapper/scroll-wrapper.component';
import { Subscription } from 'rxjs';
import { FormBuilder, FormGroup } from '@angular/forms';

import { Client } from 'src/providers/client/client';
import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { Workflows } from "src/providers/workflows/workflows";
import { WindowService } from 'src/shared/providers/window.service';
import { TranslationPipe } from 'src/shared/pipes/translation.pipe';

import { WorkflowPage } from 'src/pages/workflows/workflow/workflow';
import { EditWorkflowPage } from 'src/pages/workflows/edit-workflow/edit-workflow';
import { WorkflowHistoryPage } from 'src/pages/workflows/workflow-history/workflow-history';

import { GuestReservations } from 'src/providers/guest-reservations/guest-reservations';
import { AutomaticBookingAddressPage } from 'src/pages/more/automatic-booking/automatic-booking-address/automatic-booking-address';
import { AddAutomationPage } from 'src/pages/workflows/add-automation/add-automation';
import { ParseDateTimePipe } from 'src/shared/pipes/parse-date.pipe';

import { Util } from 'src/shared/util/util';

@Component({
    selector: 'automations-summary',
    templateUrl: 'automations-summary.html',
    encapsulation: ViewEncapsulation.None,
    styleUrls: ['automations-summary.scss'],
    standalone: false
})

export class AutomationsSummaryPage implements OnInit, OnDestroy {
  filterLoaded: boolean;
  propertyFilter: any;
  form: FormGroup;
  didLoadAllRows: boolean;
  typeFilter = 'All Automations';
  addressPage = 0;
  activeWorkflows: any;
  automaticBookingAddresses: any;
  errorMessage: string;
  loaded: boolean;
  showLoadingSpinner: boolean;
  workflows: any;
  panelClosedSubscription: Subscription;
  shouldReloadAllAutomations: boolean;
  headers = ['Name', 'Properties', 'Trigger', 'Actions', 'Status', 'last-column'];
  workflowFiltersList = [
    { viewValue: 'All Automations', value: 'All Automations' },
    { viewValue: 'Custom Workflows', value: 'Custom Workflows' },
    { viewValue: 'Auto-Schedule Workflows', value: 'Auto-Schedule Workflows' },
  ];
  propertyFiltersList = [
    { viewValue: 'All Properties', value: 'All Properties' }
  ];
  workflowFilters = [
    {
      viewValue: 'All Automations',
      show: true
    },
    {
      viewValue: 'Custom Workflows',
      show: false
    },
    {
      viewValue: 'Auto-Schedule Workflows',
      show: false
    },
  ];
  rows = []
  currentRows = [];
  addressResponse: any;
  isBuildingRows: boolean;
  showFilterChipsOnSecondRow: boolean;
  pageHeight: number;
  @ViewChild('tidyTable') tidyTable: any;
  @HostListener('window:resize', ['$event'])
  async onResize(event) {
    this.pageHeight = event.target.innerHeight;
    this.showFilterChipsOnSecondRow = event.target.innerWidth < 980;
  }

  constructor(
    private client: Client,
    private fb: FormBuilder,
    private navCtrl: CustomNavController,
    private storage: TidyStorage,
    private rightSidePanelService: RightSidePanelService,
    private translationPipe: TranslationPipe,
    private workflowsProvider: Workflows,
    public windowService: WindowService,
    private util: Util,
    private guestReservations: GuestReservations
  ) {
    this.form = this.fb.group({
      workflowFilter: ['All Automations'],
      propertyFilter: ['All Properties']
    });
  }

  async ngOnInit() {
    try {
      this.initializeFilters();
      this.pageHeight = window.innerHeight;
      this.showFilterChipsOnSecondRow = window.innerWidth < 980;
      await this.loadPage();
    } catch (err) {
      this.util.showError((err.error && err.error.message) ? err.error.message : err.message, 10000);
    }
  }

  initializeFilters() {
    this.workflowFiltersList = [
      { viewValue: this.translationPipe.transform('All Automations'), value: 'All Automations' },
      { viewValue: this.translationPipe.transform('Custom Workflows'), value: 'Custom Workflows' },
      { viewValue: this.translationPipe.transform('Auto-Schedule Workflows'), value: 'Auto-Schedule Workflows' },
    ];
    this.workflowFilters = [
      { viewValue: this.translationPipe.transform('All Automations'), show: true },
      { viewValue: this.translationPipe.transform('Custom Workflows'), show: false },
      { viewValue: this.translationPipe.transform('Auto-Schedule Workflows'), show: false },
    ];
  }

  ngOnDestroy() {
    this.cleanup();
  }

  cleanup() {
    this.addressPage = 0;
    this.loaded = false;
    this.propertyFilter = null;
    this.typeFilter = 'All Automations';
    this.didLoadAllRows = false;
    this.rows = [];
    this.currentRows = [];
    this.isBuildingRows = false;
    this.form.patchValue({
      workflowFilter: 'All Automations',
      propertyFilter: 'All Properties'
    });
  }

  async loadPage(showLoadingSpinner = false, setStoredRoute = true) {
    if (showLoadingSpinner) {
      this.showLoadingSpinner = true;
      this.cleanup();
    }
    this.addressResponse = await this.client.getMoreDetailAddresses();
    this.buildPropertyFiltersList();
    await this.buildRows();
    this.loaded = true;
    this.filterLoaded = true;
    this.showLoadingSpinner = false;
    if (this.windowService.isDesktopRes && setStoredRoute) {
      this.registerStoredRoute();
    }
  }

  private registerStoredRoute() {
    this.storage.setStoredRoute('/automations/summary').then(() => {
      this.registerStoredRoute();
    });
  }

  buildPropertyFiltersList() {
    this.propertyFiltersList = [
      { viewValue: this.translationPipe.transform('All Properties'), value: 'All Properties' }
    ];
    if (this.addressResponse && this.addressResponse.length > 0) {
      this.addressResponse.forEach(address => {
        const propertyName = address.address_name || address.address1 || `Property ${address.id}`;
        this.propertyFiltersList.push({
          viewValue: propertyName,
          value: address.id
        });
      });
    }
  }

  async buildRows() {
    let workflowRows, automaticBookingRows;
    if (this.loaded && !this.shouldReloadAllAutomations) {
      [automaticBookingRows] = await Promise.all([
        this.buildAutomaticBookingSettingsRows()
      ]);
    } else {
      [workflowRows, automaticBookingRows] = await Promise.all([
        this.buildWorkflowRows(),
        this.buildAutomaticBookingSettingsRows()
      ]);
    }
    const addressBasedRows = [...automaticBookingRows];
    const groupedByAddress = {};
    addressBasedRows.forEach(row => {
      let addressId = 'no-address';
      if (row.Properties) {
        if (Array.isArray(row.Properties) && row.Properties.length > 0) {
          addressId = row.Properties[0].addressId || 'no-address';
        } else if (row.Properties.addressId) {
          addressId = row.Properties.addressId;
        }
      }
      if (!groupedByAddress[addressId]) {
        groupedByAddress[addressId] = [];
      }
      groupedByAddress[addressId].push(row);
    });
    if (this.loaded && !this.shouldReloadAllAutomations) {
      this.rows = [
        ...this.rows,
        ...Object.values(groupedByAddress).flat()
      ]
    } else {
      this.rows = [
        ...workflowRows,
        ...Object.values(groupedByAddress).flat()
      ]
    }
    this.shouldReloadAllAutomations = false;
    this.filterCurrentRows();
  }

  async buildWorkflowRows() {
    const rows = [];
    this.workflows = await this.workflowsProvider.getWorkflowsWithVersions();
    this.workflows.forEach((workflow) => {
      let properties = [];
      const version = workflow.published_version || workflow.draft_version;
      if (version?.workflow_version_trigger?.input_values?.address_ids) {
        version?.workflow_version_trigger?.input_values?.address_ids.forEach((addressId) => {
          const address = this.addressResponse.find((addr) => addr.id === addressId);
          if (address) {
            properties.push({
              value: address.address_name || address.address1,
              color: address.color,
              addressId: address.id,
              action: null,
            })
          }
        })
      }
      if (properties.length === 0) {
        properties = [{
          value: 'All Properties',
          action: null,
          icon: 'assets/img/globe-outline-black.svg',
        }];
      }
      const actions = [];
      version?.workflow_version_actions?.forEach((action) => {
        actions.push({
          value: action.name,
          action: null,
          icon: action.icon_url,
        })
      });
      rows.push({
        'Properties': properties,
        'Status': {
          value: workflow.paused_at ? 'Disabled' : 'Enabled',
        },
        'Name': {
          value: workflow.name,
          action: null,
        },
        'Trigger': {
          value: version?.workflow_version_trigger?.name,
          icon: version?.workflow_version_trigger?.icon_url,
          action: null,
        },
        'Actions': actions,
        'last-column': {
          value: '',
          action: null,
        },
        'type': {
          type: 'workflow',
          workflow_id: workflow.id,
          address_ids: version?.workflow_version_trigger?.input_values?.address_ids || [],
        }
      });
    });
    return rows;
  }

  async buildAutomaticBookingSettingsRows() {
    const rows = [];
    const addressIds = this.propertyFilter ? [this.propertyFilter] : this.addressResponse.map((address) => address.id).slice(this.addressPage, this.addressPage + 7);
    const addressIdsString = addressIds.join(',');
    const params = {
      filters: {
        address_ids: addressIdsString,
      }
    };
    const queryString = this.buildQueryString(params);
    let automaticBookingSettings = await this.guestReservations.getAutomaticBookingSettingsForAddresses(queryString);
    automaticBookingSettings = automaticBookingSettings.map((setting) => {
      setting.address = this.addressResponse.find((addr) => addr.address_automatic_booking_setting?.id === setting.id);
      return setting;
    })
    automaticBookingSettings.forEach((setting) => {
      const address = this.addressResponse.find((addr) => addr.address_automatic_booking_setting?.id === setting.id);
      if (!address) {
        return;
      }
      const timeRange = setting.booking_start_time_no_earlier_than ? `${new ParseDateTimePipe().transform(setting.booking_start_time_no_earlier_than, 'h:mma')} - ${new ParseDateTimePipe().transform(setting.booking_end_time_no_later_than, 'h:mma')}` : ''; 
      rows.push({
        'Properties': {
          value: address.address_name || address.address1,
          color: address.color,
          addressId: address.id,
          action: null,
        },
        'Status': {
          value: setting.automatic_booking_enabled ? 'Enabled' : 'Disabled',
        },
        'Name': {
          value: 'Auto-Schedule Workflow',
          action: null,
        },
        'Trigger': {
          value: 'Reservation created',
          icon: 'https://retool-uploads.s3.amazonaws.com/booking%20(2)%20(1).svg',
          action: null,
        },
        'Actions':
          {
            value: `${new TranslationPipe().transform('Book')} ${setting.required_team_service.name} (${timeRange} ${new TranslationPipe().transform('no more than')} ${setting.book_when_days_left_to_check_in_date_is_within} ${new TranslationPipe().transform('days before check in')} ${new TranslationPipe().transform('and')} ${setting.max_days_to_perform_job_after_checkout} ${new TranslationPipe().transform('days after check out')})`,
            action: null,
            icon: 'assets/img/briefcase.svg',
            truncate: true
          }
        ,
        'last-column': {
          value: '',
          action: null,
        },
        'type': {
          type: 'automatic-booking',
          address_ids: [address.id],
        }
      });
    });
    return rows;
  }

  buildQueryString(params: any): string {
    const queryParts: string[] = [];
    if (params.page) {
      queryParts.push(`page=${params.page}`);
    }
    if (params.per_page) {
      queryParts.push(`per_page=${params.per_page}`);
    }
    if (params.filters) {
      if (params.filters.address_ids) {
        queryParts.push(`filters[address_ids]=${params.filters.address_ids}`);
      }
      if (params.filters.service_type_ids) {
        queryParts.push(`filters[service_type_ids]=${params.filters.service_type_ids}`);
      }
      if (params.filters.only_private_homekeepers !== undefined) {
        queryParts.push(`filters[only_private_homekeepers]=${params.filters.only_private_homekeepers}`);
      }
    }
    return queryParts.length > 0 ? `?${queryParts.join('&')}` : '';
  }

  
  async pageChange() {
    if (this.typeFilter === 'Custom Workflows' || this.propertyFilter) {
      return;
    }
    this.addressPage += 7;
    if (this.addressPage >= this.addressResponse.length) {
      return this.didLoadAllRows = true;
    }
    if (this.isBuildingRows) {
      return;
    }
    if (this.tidyTable) {
      this.tidyTable.isLoading = true;
    }
    this.isBuildingRows = true;
    try {
      await this.buildRows();
    } finally {
      if (this.tidyTable) {
        this.tidyTable.isLoading = false;
      }
      this.isBuildingRows = false;
    }
  }

  changeWorkflowFilter(value) {
    this.typeFilter = value;
    this.workflowFilters.forEach(filter => {
      filter.show = filter.viewValue === value;
    });
    this.filterCurrentRows();
  }

  filterCurrentRows() {
    if (!this.typeFilter || this.typeFilter === 'All Automations') {
      this.currentRows = this.rows;
    } else if (this.typeFilter === 'Custom Workflows') {
      this.currentRows = this.rows.filter(row => row.type?.type === 'workflow');
    } else if (this.typeFilter === 'Auto-Schedule Workflows') {
      this.currentRows = this.rows.filter(row => row.type?.type === 'automatic-booking');
    }
    if (this.propertyFilter) {
      this.currentRows = this.currentRows.filter(row => row.type?.address_ids.includes(this.propertyFilter));
    }
  }

  async changePropertyFilter(value) {
    if (value === 'All Properties') {
      this.propertyFilter = null;
      this.addressPage = 0;
    } else {
      this.propertyFilter = value;
    }
    this.filterLoaded = false;
    this.rows = [];
    await this.buildRows();
    this.filterLoaded = true;
  }

  async goToPage(url, params, component) {
    const isMobileResolution = !this.windowService.isDesktopRes;
    if (!isMobileResolution) {
      const isRightSidePanelAlreadyOpen = await this.storage.retrieve('dialog-right-side-open');
      if (isRightSidePanelAlreadyOpen) {
        return this.rightSidePanelService.navigateTo(url, params, component);
      } else {
        this.storage.save('dialog-right-side-open', true);
        this.storage.save('dialog-params', params);
        this.rightSidePanelService.openRightSidePanel(component);
      }
      this.panelClosedSubscription = this.rightSidePanelService.panelClosed().subscribe(async () => {
        this.shouldReloadAllAutomations = await this.storage.retrieve('shouldReloadAllAutomations');
        if (this.shouldReloadAllAutomations) {
          await this.loadPage(true);
          await this.storage.delete('shouldReloadAllAutomations');
        }
      });
    } else {
      this.navCtrl.navigateForward(url, params);
    }
  }

  goToWorkflowHistory() {
    this.goToPage('automations/workflow-history', {}, WorkflowHistoryPage);
  }

  goToAddAutomationPage() {
    this.goToPage('add-automation', {numberOfWorkflows: this.workflows.length}, AddAutomationPage);
  }

  async goToAutomation(event) {
    if (event.element.type.type === 'workflow') {
      const workflowId = event.element.type.workflow_id;
      await this.storage.save('workflowId', workflowId);
      const workflow = this.workflows.find((w) => w.id === workflowId);
      const params = {
        workflow
      }
      if (workflow.published_version) {
        this.goToPage(`/automations/workflow/${workflowId}`, params, WorkflowPage);
      } else {
        this.goToPage(`/automations/workflow/${workflowId}/edit`, params, EditWorkflowPage);
      }
    } else if (event.element.type.type === 'automatic-booking') {
      const addressId = event.element.type.address_ids[0];
      const address = this.addressResponse.find((addr) => addr.id === addressId);
      this.goToPage(`/automatic-booking-property/${addressId}`, {address}, AutomaticBookingAddressPage);
    }
  }

}
